import { useQueryClient } from '@tanstack/react-query'
import 'react-toastify/dist/ReactToastify.css'
import { createRestClient } from '@/contexts/QueryProvider'
import { useGetSearchParam, useSessionStorage } from '@/hooks'
import { ErrorComponent } from '../ErrorComponent'
import { toast } from 'react-toastify'
import { Button, SkeletonItem } from '@fluentui/react-components'
import {
  ArrowSync24Regular,
  FlowRegular,
  Notebook24Regular,
  Play24Regular,
  TasksAppRegular,
} from '@fluentui/react-icons'
import { useState } from 'react'
import { ENDPOINTS } from '@/util/endpoints'

export const WorkspaceDatabricksPanel: React.FC = () => {
  const queryClient = useQueryClient()
  const orgName = useGetSearchParam<string>('orgName')
  //const orgId = useGetSearchParam<number>('orgId')
  const workspaceId = useGetSearchParam<number>('subscriptionId')
  const projectName = useGetSearchParam<string>('projectName')
  const [provisioningWorkspace, setProvisioningWorkspace] = useState<boolean>()
  const [syncingWorkspace, setSyncingWorkspace] = useState<boolean>()
  const [token] = useSessionStorage('token', '')

  const {
    getDatabricksApiGetDatabricksWorkspaceStatus,
    putDatabricksApiCreateDatabricksWorkspace,
    putDatabricksApiSyncDatabricksWorkspaceJobs,
    putDatabricksApiRunDatabricksWorkspaceJobs,
  } = createRestClient()

  const invalidateWorkspace = () => getDatabricksApiGetDatabricksWorkspaceStatus.invalidateQueries(queryClient)
  const { mutateAsync: provisionWorkspace } = putDatabricksApiCreateDatabricksWorkspace.useMutation()
  const { mutateAsync: syncWorkspace } = putDatabricksApiSyncDatabricksWorkspaceJobs.useMutation()
  const { mutateAsync: runJob } = putDatabricksApiRunDatabricksWorkspaceJobs.useMutation()

  const {
    data: workspace,
    isPending: workspacePending,
    isError: workspaceError,
  } = getDatabricksApiGetDatabricksWorkspaceStatus.useQuery(
    {
      query: { workspaceId },
    },
    {
      enabled: !!workspaceId,
    }
  )

  const onProvisionWorkspace = () => {
    toast.info('Started provisioning workspace')
    setProvisioningWorkspace(true)

    provisionWorkspace(
      { query: { workspaceId: workspaceId } },
      {
        onError: (error) => {
          toast.error('Error provisioning workspace: ' + error)
        },
        onSuccess: () => {
          toast.success('Provisioned workspace')
        },
        onSettled: () => {
          invalidateWorkspace()
          setProvisioningWorkspace(false)
        },
      }
    )
  }

  const onPowerBi = async () => {
    const response = await fetch(
      `${ENDPOINTS.LANDING}/databricks/workspace/powerbi?workspaceId=${workspace?.workspaceId}`,
      {
        headers: { Authorization: 'Bearer ' + token },
      }
    )
    if (response.ok) {
      const blob = await response.blob()
      const objectUrl = window.URL.createObjectURL(blob)

      const filename = `workspace-${workspace?.projectName}-${workspace?.workspaceId}.pbids`

      const anchor = document.createElement('a')
      document.body.appendChild(anchor)
      anchor.setAttribute('href', objectUrl)
      anchor.setAttribute('download', filename)
      anchor.click()
      window.URL.revokeObjectURL(objectUrl)
    } else {
      let errMessage = 'Unknown error'
      if (response.statusText) errMessage = response.statusText
      try {
        const tryJson = await response.json()
        if (tryJson.Message) errMessage = tryJson.Message
      } catch (_error) {
        // ignore
      }
      toast.error('Error downloading file: ' + errMessage)
    }
  }

  const onSync = () => {
    setSyncingWorkspace(true)
    syncWorkspace(
      { query: { workspaceId: workspaceId } },
      {
        onError: (error) => {
          toast.error('Error syncing workspace: ' + error)
        },
        onSuccess: () => {
          toast.success('Sync done')
        },
        onSettled: () => {
          invalidateWorkspace()
          setSyncingWorkspace(false)
        },
      }
    )
  }

  const onRun = (jobId: number) => {
    runJob(
      { query: { workspaceId: workspaceId, jobId: jobId } },
      {
        onError: (error) => {
          toast.error('Error running job: ' + error)
        },
        onSuccess: () => {
          toast.success('Job running')
        },
        onSettled: () => {},
      }
    )
  }

  //if (workspacePending) return <Loading />
  if (workspaceError) return <ErrorComponent />

  const skeletonContent = (
    <>
      <div className="grid grid-cols-1 gap-4 py-3 sm:grid-cols-2 lg:grid-cols-3 2xl:grid-cols-4">
        <div className="grid grid-cols-[1fr_4fr_1fr] gap-2 rounded-lg bg-white p-4 shadow">
          <div className="flex h-12 w-12 flex-shrink-0 flex-grow-0 items-center justify-center self-start rounded-full bg-grey-lightest p-2 text-grey-dark">
            <SkeletonItem shape="circle" size={24} />
          </div>
          <div className="flex flex-col justify-between gap-1">
            <div className="text-base">
              <SkeletonItem size={16} className="width-200" style={{ width: '100px' }} />
            </div>
            <div className="text-xs font-light">
              <SkeletonItem size={12} className="width-200" style={{ width: '100px' }} />
            </div>
          </div>
          <div className="col-span-2 col-start-2 flex flex-row justify-end gap-2 border-t pt-3">
            <SkeletonItem size={12} className="width-60" style={{ width: '60px' }} />
            <SkeletonItem size={12} className="width-60" style={{ width: '60px' }} />
            <SkeletonItem size={12} className="width-60" style={{ width: '60px' }} />
          </div>
        </div>
      </div>
    </>
  )

  let generalDetails: Array<{ label: string; value?: string }> = []

  let jobsContent
  if (workspacePending || workspace === undefined) {
    jobsContent = skeletonContent
  } else {
    const { applicationName, jobs } = workspace

    generalDetails = [
      { label: 'Organisation', value: orgName },
      { label: 'Project', value: projectName },
      { label: 'Application', value: applicationName || '' },
    ]

    if (jobs == null || jobs.length === 0)
      jobsContent = (
        <div className="flex gap-4 py-3">No jobs found. Try Sync to create jobs from production notebooks.</div>
      )
    else
      jobsContent = (
        <>
          <div className="grid grid-cols-1 gap-4 py-3 sm:grid-cols-2 lg:grid-cols-3 2xl:grid-cols-4">
            {jobs!.map(({ jobId, jobName, taskUrl, jobUrl, notebook }) => (
              <div key={jobId} className="grid grid-cols-[1fr_4fr_1fr] gap-2 rounded-lg bg-white p-4 shadow">
                <div className="flex h-12 w-12 flex-shrink-0 flex-grow-0 items-center justify-center self-start rounded-full bg-grey-lightest p-2 text-grey-dark">
                  <FlowRegular />
                </div>
                <div className="flex flex-col justify-between gap-1">
                  <div className="text-base">{jobName}</div>
                  <div className="text-xs font-light">{notebook}</div>
                </div>
                <div className="col-span-2 col-start-2 flex flex-row justify-end gap-2 border-t pt-3">
                  <Button
                    appearance="primary"
                    size="small"
                    icon={<Play24Regular />}
                    onClick={() => {
                      onRun(jobId!)
                    }}
                  >
                    Run
                  </Button>

                  {jobUrl && (
                    <Button size="small" icon={<Notebook24Regular />} onClick={() => window.open(jobUrl, '_blank')}>
                      Edit Job
                    </Button>
                  )}
                  {taskUrl && (
                    <Button size="small" icon={<TasksAppRegular />} onClick={() => window.open(taskUrl, '_blank')}>
                      Edit Task
                    </Button>
                  )}
                </div>
              </div>
            ))}
          </div>
        </>
      )
  }

  return (
    <>
      <div className="flex flex-col gap-y-5 py-4">
        <div className="w-full space-y-4">
          <div className="flex w-full flex-col gap-y-4 rounded bg-white p-6 pt-4">
            <h6 className="border-b">Workspace details</h6>
            <div className="flex shrink-0 flex-row flex-wrap gap-x-20 gap-y-4">
              {generalDetails.map(({ label, value }) => (
                <div className="flex flex-col whitespace-nowrap" key={label}>
                  <span className="text-sm font-light">{label}:</span>
                  <span className="text-base">{value}</span>
                </div>
              ))}
            </div>
          </div>
        </div>

        <div className="flex flex-col gap-y-5 py-4">
          <div className="w-full space-y-4">
            <div className="flex w-full flex-col gap-y-4 rounded bg-white p-6 pt-4">
              <div className="flex shrink-0 flex-row flex-wrap gap-x-20 gap-y-4">
                {workspace?.message && (
                  <div className="flex flex-col gap-2 whitespace-nowrap">
                    <span className="text-sm">Warning:</span>
                    <span className="flex flex-col gap-2 text-base">
                      <span className="text">{workspace?.message}</span>
                    </span>
                  </div>
                )}

                <div className="flex flex-col gap-2 whitespace-nowrap">
                  <span className="text-sm">Provision Workspace:</span>
                  <span className="flex flex-col gap-2 text-base">
                    <Button
                      appearance="secondary"
                      disabled={provisioningWorkspace}
                      onClick={() => onProvisionWorkspace()}
                    >
                      Provision Workspace
                    </Button>
                    <span className="text-sm font-light">Set up folder on Databricks and copy app notebook/s</span>
                  </span>
                </div>

                <div className="flex flex-col gap-2 whitespace-nowrap">
                  <span className="text-sm">Connect to PowerBI:</span>
                  <span className="flex flex-col gap-2 text-base">
                    <Button appearance="secondary" onClick={() => onPowerBi()} disabled={!workspace?.workspaceId}>
                      Export .pbids
                    </Button>
                    <span className="text-sm font-light">Download a PowerBI datasource connection file</span>
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div className="flex items-center justify-between border-b pb-1">
        <div>
          <h5>Jobs</h5>
        </div>
        <Button
          appearance={workspace?.jobs?.length === 0 ? 'primary' : 'transparent'}
          icon={<ArrowSync24Regular />}
          disabled={syncingWorkspace}
          onClick={() => onSync()}
        />
      </div>
      {jobsContent}
    </>
  )
}
